// PermitDetailDrawer — slides in from the right; shows full permit detail,
// status transition controls, documents, fees, history, and (for master
// permits) the full list of crossings covered by the agreement with CSV /
// GeoJSON exports for permit-application exhibits.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader from './DocumentUploader';

const KIND_LABELS: Record<string, string> = {
  nepa: 'NEPA',
  section_404: '§404 (Clean Water Act)',
  section_106: '§106 (Historic Preservation)',
  tribal_consultation: 'Tribal Consultation',
  esa_section_7: 'ESA §7',
  floodplain: 'Floodplain',
  mbta: 'Migratory Bird Treaty',
  railroad: 'Railroad Crossing',
  dot_access: 'DOT Access',
  highway: 'Highway Crossing',
  row_county: 'County ROW',
  row_city: 'City ROW',
  pole_attach: 'Pole Attachment',
  make_ready: 'Make-Ready',
  building: 'Building Permit',
  other: 'Other',
};

const CROSSING_KIND_LABEL: Record<string, string> = {
  waterway:   'Waterway',
  highway:    'State highway',
  local_road: 'Local road',
  railroad:   'Railroad',
  other:      'Other',
};
const CROSSING_KIND_COLOR: Record<string, string> = {
  waterway:   '#1d4ed8',
  highway:    '#b45309',
  local_road: '#475569',
  railroad:   '#111827',
  other:      '#94a3b8',
};

const STATUS_OPTIONS = [
  { v: 'needed',    l: 'Needed (apply)' },
  { v: 'applied',   l: 'Applied' },
  { v: 'in_review', l: 'In review' },
  { v: 'issued',    l: 'Issued' },
  { v: 'approved',  l: 'Approved' },
  { v: 'denied',    l: 'Denied' },
  { v: 'waived',    l: 'Waived' },
  { v: 'expired',   l: 'Expired' },
  { v: 'revoked',   l: 'Revoked' },
  { v: 'n_a',       l: 'N/A' },
];

export default function PermitDetailDrawer({
  permitId, onClose, onUpdated,
}: {
  permitId: string;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const qc = useQueryClient();

  const { data: permit, refetch } = useQuery<any>({
    queryKey: ['permit', permitId],
    queryFn: () => api.get(`/permits/${permitId}`),
  });

  const [newStatus, setNewStatus] = useState<string>('');
  const [statusReason, setStatusReason] = useState('');
  const [permitNumber, setPermitNumber] = useState('');
  const [editingNumber, setEditingNumber] = useState(false);

  const [uploadDocKind, setUploadDocKind] = useState<string>('application');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  // Show full crossings table only on demand (lots of rows for big master permits).
  const [crossingsExpanded, setCrossingsExpanded] = useState(false);

  // Inline per-crossing edits. Holds an optimistic copy of any crossing the
  // operator has just touched (until the refetch lands).
  const crossingMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/permits/crossings/${id}`, patch),
    onSuccess: () => { refetch(); onUpdated(); },
  });

  // Generic field-level PATCH for the permit row (dates, notes, feature_name,
  // etc.). Used by the inline date pickers and the notes editor.
  const patchMut = useMutation({
    mutationFn: (patch: any) => api.patch(`/permits/${permitId}`, patch),
    onSuccess: () => {
      refetch();
      onUpdated();
      qc.invalidateQueries({ queryKey: ['construction-readiness'] });
      qc.invalidateQueries({ queryKey: ['as-built-readiness'] });
      qc.invalidateQueries({ queryKey: ['permits-summary'] });
    },
  });

  // Helper: pull just the YYYY-MM-DD portion off an ISO/timestamp string so
  // <input type="date"> displays correctly. The server stores these as date
  // (no time component) but stringifies with a midnight time.
  function dateOnly(v: string | null | undefined): string {
    if (!v) return '';
    const s = String(v);
    return s.length >= 10 ? s.slice(0, 10) : s;
  }

  // Local-state editor for the notes textarea (so typing doesn't fire a PATCH
  // on every keystroke — saves on blur or Cmd-Enter).
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const notesValue = notesDraft ?? (permit?.notes ?? '');

  const attachDocMut = useMutation({
    mutationFn: (documentId: string) =>
      api.post(`/permits/${permitId}/documents`, {
        doc_kind: uploadDocKind,
        document_id: documentId,
        notes: uploadNotes || undefined,
      }),
    onSuccess: () => {
      refetch();
      onUpdated();
      setUploadOpen(false);
      setUploadNotes('');
    },
  });

  const statusMut = useMutation({
    mutationFn: () => api.post(`/permits/${permitId}/status`, { to_status: newStatus, reason: statusReason || undefined }),
    onSuccess: () => {
      refetch();
      onUpdated();
      // Invalidate every query whose data depends on permit status — Construction
      // readiness, As-built readiness, summary cards, crossing pins, compliance
      // summary. Without this, the As-built map and Compliance Overview keep
      // showing pre-transition state until the user reloads the tab.
      qc.invalidateQueries({ queryKey: ['construction-readiness'] });
      qc.invalidateQueries({ queryKey: ['as-built-readiness'] });
      qc.invalidateQueries({ queryKey: ['permit-crossings'] });
      qc.invalidateQueries({ queryKey: ['permits-summary'] });
      qc.invalidateQueries({ queryKey: ['dash-permit-summary'] });
      qc.invalidateQueries({ queryKey: ['comp-summary'] });
      qc.invalidateQueries({ queryKey: ['obligations'] });
      setNewStatus('');
      setStatusReason('');
    },
  });

  const numberMut = useMutation({
    mutationFn: () => api.patch(`/permits/${permitId}`, { permit_number: permitNumber }),
    onSuccess: () => {
      refetch();
      onUpdated();
      setEditingNumber(false);
    },
  });

  if (!permit) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.3)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 460, background: '#fff', padding: 30, textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }

  const meta = permit.metadata ?? {};
  const crossings: any[] = permit.crossings ?? [];
  const hasCrossings = crossings.length > 0;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.3)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 540, maxWidth: '95%', background: '#fff', height: '100vh', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', background: '#0f172a', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {permit.bucket}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
                {KIND_LABELS[permit.kind] ?? permit.kind}
              </div>
              {permit.feature_name && (
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                  {permit.feature_name}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
          </div>
        </div>

        {/* Status + transition */}
        <div style={{ padding: 16, background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Status</span>
            <span style={{
              padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: '#fff', border: '1px solid #cbd5e1',
            }}>{(permit.status ?? '').replace(/_/g, ' ').toUpperCase()}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
              style={{ flex: 1, padding: '6px 10px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4 }}>
              <option value="">Change status to…</option>
              {STATUS_OPTIONS.filter(s => s.v !== permit.status).map(s => (
                <option key={s.v} value={s.v}>{s.l}</option>
              ))}
            </select>
            <button onClick={() => statusMut.mutate()} disabled={!newStatus || statusMut.isPending}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 600,
                background: newStatus ? '#1e3a8a' : '#cbd5e1',
                color: '#fff', border: 0, borderRadius: 4,
                cursor: newStatus ? 'pointer' : 'not-allowed',
              }}>
              {statusMut.isPending ? '…' : 'Apply'}
            </button>
          </div>
          {newStatus && (
            <input type="text" value={statusReason} onChange={e => setStatusReason(e.target.value)}
              placeholder="Optional reason / note for the audit trail"
              style={{ width: '100%', marginTop: 6, padding: '5px 8px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 4 }} />
          )}
        </div>

        {/* Detail sections */}
        <div style={{ padding: 16 }}>
          {/* Identity */}
          <Section title="Identity">
            <Row label="Kind" value={KIND_LABELS[permit.kind] ?? permit.kind} />
            <Row label="Bucket" value={permit.bucket} />
            <Row label="Jurisdiction" value={permit.jurisdiction ?? '—'} />
            <Row label="Anchor" value={
              permit.project_only ? 'Project-wide' :
              permit.segment_id ? 'Segment ' + permit.segment_id.slice(0, 8) :
              permit.pole_id ? 'Pole ' + permit.pole_id.slice(0, 8) :
              permit.structure_id ? 'Structure ' + permit.structure_id.slice(0, 8) :
              permit.vault_id ? 'Vault ' + permit.vault_id.slice(0, 8) :
              '—'
            } />
            <Row label="Permit #" value={
              editingNumber ? (
                <span style={{ display: 'flex', gap: 4 }}>
                  <input value={permitNumber} onChange={e => setPermitNumber(e.target.value)}
                    placeholder="e.g. NWO-2026-00451"
                    style={{ flex: 1, padding: '3px 6px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3 }} />
                  <button onClick={() => numberMut.mutate()} disabled={numberMut.isPending}
                    style={{ padding: '3px 8px', fontSize: 11, background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}>
                    Save
                  </button>
                  <button onClick={() => setEditingNumber(false)}
                    style={{ padding: '3px 8px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </span>
              ) : (
                <span>
                  {permit.permit_number ?? <i style={{ color: '#94a3b8' }}>not assigned</i>}
                  <button onClick={() => { setEditingNumber(true); setPermitNumber(permit.permit_number ?? ''); }}
                    style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}>
                    {permit.permit_number ? 'edit' : '+ set'}
                  </button>
                </span>
              )
            } />
          </Section>

          {/* Agency */}
          {permit.agency_name && (
            <Section title="Agency">
              <Row label="Name" value={permit.agency_name} />
              {permit.agency_short_name && <Row label="Short name" value={permit.agency_short_name} />}
              {permit.agency_external_code && <Row label="Code" value={permit.agency_external_code} />}
              <Row label="Kind" value={permit.agency_kind ?? '—'} />
            </Section>
          )}

          {/* Lifecycle — inline date pickers. Applied/Issued are stamped
              automatically by status transitions but the operator can override
              for back-dated entries. Expires_at must be set manually. */}
          <Section title="Lifecycle dates">
            {([
              ['needed_at',  'Needed at',  'When the obligation appeared'],
              ['applied_at', 'Applied at', 'Auto-set when status → Applied'],
              ['issued_at',  'Issued at',  'Auto-set when status → Issued'],
              ['expires_at', 'Expires at', 'Drives the "expiring soon" alert'],
            ] as const).map(([field, label, hint]) => (
              <div key={field} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', fontSize: 12 }}>
                <span style={{ width: 110, color: '#64748b', flexShrink: 0 }}>{label}</span>
                <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="date"
                    value={dateOnly((permit as any)[field])}
                    disabled={patchMut.isPending}
                    onChange={e => patchMut.mutate({ [field]: e.target.value || null })}
                    style={{
                      flex: 1, maxWidth: 160,
                      padding: '3px 6px', fontSize: 12,
                      border: '1px solid #cbd5e1', borderRadius: 3,
                      color: '#0f172a', background: '#fff',
                    }}
                  />
                  {(permit as any)[field] && (
                    <button
                      onClick={() => patchMut.mutate({ [field]: null })}
                      disabled={patchMut.isPending}
                      title="Clear this date"
                      style={{
                        padding: '2px 6px', fontSize: 10, background: '#fff',
                        border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer',
                        color: '#64748b',
                      }}
                    >clear</button>
                  )}
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{hint}</span>
                </span>
              </div>
            ))}
          </Section>

          {/* Crossings — only on master permits (highway, row_city, etc.). This
              is what an operator attaches to the agency's application packet. */}
          {hasCrossings && (
            <Section title={`Crossings covered by this permit (${crossings.length})`}>
              <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, marginBottom: 8 }}>
                Physical locations covered by this master agreement. Attach the
                <b> Exhibit CSV</b> to your application package as "Exhibit A —
                Crossing Schedule".
              </div>

              {/* Export buttons */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <a
                  href={`/api/permits/${permitId}/exhibit.csv`}
                  download
                  style={{
                    padding: '6px 12px', fontSize: 11, fontWeight: 600,
                    background: '#1e3a8a', color: '#fff', borderRadius: 4,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >📄 Exhibit CSV</a>
                <a
                  href={`/api/permits/${permitId}/exhibit.geojson`}
                  download
                  style={{
                    padding: '6px 12px', fontSize: 11, fontWeight: 600,
                    background: '#fff', color: '#1e3a8a', border: '1px solid #1e3a8a', borderRadius: 4,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >🗺 GeoJSON</a>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '6px 12px', fontSize: 11, fontWeight: 600,
                    background: '#fff', color: '#475569', border: '1px solid #cbd5e1',
                    borderRadius: 4, cursor: 'pointer',
                  }}
                >🖨 Print</button>
                <button
                  onClick={() => setCrossingsExpanded(x => !x)}
                  style={{
                    padding: '6px 12px', fontSize: 11, fontWeight: 600,
                    background: '#fff', color: '#475569', border: '1px solid #cbd5e1',
                    borderRadius: 4, cursor: 'pointer', marginLeft: 'auto',
                  }}
                >{crossingsExpanded ? `▾ Hide list` : `▸ Show list (${crossings.length})`}</button>
              </div>

              {/* Kind breakdown */}
              <CrossingBreakdown crossings={crossings} />

              {/* Full table */}
              {crossingsExpanded && (
                <div style={{ marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 4, maxHeight: 320, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                      <tr>
                        <th style={th}>#</th>
                        <th style={th}>Feature</th>
                        <th style={th}>Kind</th>
                        <th style={{ ...th, textAlign: 'right' }}>Lat,&nbsp;Lng</th>
                        <th style={th}></th>
                        <th style={th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crossings.map((x: any, i: number) => {
                        const coords = x.geom?.coordinates;
                        // GeoJSON is [lng, lat] — humans (and Google Maps) want lat,lng.
                        const lng = coords ? coords[0] : null;
                        const lat = coords ? coords[1] : null;
                        const mapsUrl = (lat != null && lng != null)
                          ? `https://www.google.com/maps?q=${lat},${lng}`
                          : null;
                        return (
                          <tr key={x.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ ...td, color: '#94a3b8' }}>{i + 1}</td>
                            <td style={td}>{x.feature_name ?? '—'}</td>
                            <td style={td}>
                              <span style={{
                                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                background: CROSSING_KIND_COLOR[x.crossing_kind] ?? '#94a3b8',
                                marginRight: 4, verticalAlign: 'middle',
                              }} />
                              {CROSSING_KIND_LABEL[x.crossing_kind] ?? x.crossing_kind}
                            </td>
                            <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 10, color: '#475569' }}>
                              {lat != null && lng != null ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '—'}
                            </td>
                            <td style={td}>
                              {mapsUrl && (
                                <a href={mapsUrl} target="_blank" rel="noreferrer"
                                  title="Open in Google Maps"
                                  style={{ fontSize: 10, color: '#1e3a8a', textDecoration: 'none' }}>
                                  📍 view
                                </a>
                              )}
                            </td>
                            <td style={td}>
                              <select
                                value={x.status}
                                disabled={crossingMut.isPending}
                                onChange={e => crossingMut.mutate({ id: x.id, patch: { status: e.target.value } })}
                                onClick={e => e.stopPropagation()}
                                style={{
                                  fontSize: 9, padding: '2px 4px', borderRadius: 3,
                                  border: '1px solid',
                                  borderColor: x.status === 'completed' ? '#86efac' :
                                               x.status === 'documented' ? '#93c5fd' :
                                               x.status === 'waived'     ? '#cbd5e1' : '#fbbf24',
                                  background: x.status === 'completed' ? '#dcfce7' :
                                              x.status === 'documented' ? '#dbeafe' :
                                              x.status === 'waived'     ? '#e2e8f0' : '#fef3c7',
                                  color:      x.status === 'completed' ? '#166534' :
                                              x.status === 'documented' ? '#1e40af' :
                                              x.status === 'waived'     ? '#475569' : '#92400e',
                                  fontWeight: 700, textTransform: 'uppercase',
                                  cursor: crossingMut.isPending ? 'wait' : 'pointer',
                                }}
                              >
                                <option value="pending">pending</option>
                                <option value="documented">documented</option>
                                <option value="completed">completed</option>
                                <option value="waived">waived</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* Per-kind metadata */}
          {meta && Object.keys(meta).length > 0 && (
            <Section title="Kind-specific metadata">
              {Object.entries(meta).map(([k, v]) => (
                <Row key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
              ))}
            </Section>
          )}

          {/* Documents */}
          <Section title={`Documents (${permit.documents?.length ?? 0})`}>
            {permit.documents?.length > 0 ? (
              permit.documents.map((d: any) => (
                <div key={d.id} style={{ padding: '6px 0', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span>
                      <b style={{ textTransform: 'uppercase', fontSize: 9, color: '#475569', letterSpacing: '.04em' }}>{d.doc_kind}</b>{' '}
                      {d.filename ?? 'untitled'}
                      {d.size_bytes && <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 10 }}>· {fmtBytes(d.size_bytes)}</span>}
                    </span>
                    {d.document_id && (
                      <a href={`/api/bead/documents/${d.document_id}`} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>view ↗</a>
                    )}
                  </div>
                  {d.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{d.notes}</div>}
                </div>
              ))
            ) : (
              <div style={{ padding: 6, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                No documents attached yet.
              </div>
            )}

            {!uploadOpen ? (
              <button
                onClick={() => setUploadOpen(true)}
                style={{
                  marginTop: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer',
                }}
              >📤 Attach document</button>
            ) : (
              <div style={{ marginTop: 10, padding: 12, border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Attach a new document
                </div>

                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Document kind</label>
                <select value={uploadDocKind} onChange={e => setUploadDocKind(e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, marginBottom: 8 }}>
                  <option value="application">Application (what you sent)</option>
                  <option value="application_draft">Application — DRAFT</option>
                  <option value="response">Response / RFI / RAI from agency</option>
                  <option value="issuance">Issuance (the actual permit)</option>
                  <option value="denial">Denial letter</option>
                  <option value="amendment">Amendment</option>
                  <option value="renewal">Renewal documentation</option>
                  <option value="mitigation_plan">Mitigation plan (§404 / §106 MOA / etc.)</option>
                  <option value="exhibit">Crossing exhibit (Exhibit A list)</option>
                  <option value="photo">Site photo</option>
                  <option value="other">Other</option>
                </select>

                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>File</label>
                <DocumentUploader
                  value={null}
                  onChange={(documentId) => {
                    if (documentId) attachDocMut.mutate(documentId);
                  }}
                  projectId={permit.project_id}
                  docType={`permit_${uploadDocKind}`}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,image/*"
                />

                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginTop: 8, marginBottom: 2 }}>Notes (optional)</label>
                <input value={uploadNotes} onChange={e => setUploadNotes(e.target.value)}
                  placeholder="Context for this document"
                  style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3 }} />

                <div style={{ marginTop: 10, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setUploadOpen(false); setUploadNotes(''); }}
                    style={{ padding: '5px 12px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}
                  >Cancel</button>
                </div>
                {attachDocMut.isPending && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#475569' }}>Attaching…</div>
                )}
                {attachDocMut.isError && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#991b1b' }}>
                    Attach failed: {(attachDocMut.error as any)?.message ?? 'unknown error'}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* Fees */}
          {permit.fees?.length > 0 && (
            <Section title={`Fees (${permit.fees.length})`}>
              {permit.fees.map((f: any) => (
                <div key={f.id} style={{ padding: '6px 0', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span><b>${Number(f.amount_usd).toFixed(2)}</b> {(f.fee_kind ?? '').replace(/_/g, ' ')}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>{f.reimbursement_status}</span>
                  </div>
                  {f.paid_date && <div style={{ fontSize: 10, color: '#64748b' }}>paid {f.paid_date}</div>}
                </div>
              ))}
            </Section>
          )}

          {/* Status history */}
          {permit.status_history?.length > 0 && (
            <Section title={`Status history (${permit.status_history.length})`}>
              {permit.status_history.map((h: any) => (
                <div key={h.id} style={{ padding: '6px 0', fontSize: 11, borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <span style={{ color: '#94a3b8' }}>{h.from_status ?? '(initial)'}</span>
                    {' → '}
                    <b>{h.to_status}</b>
                  </div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>
                    {new Date(h.occurred_at).toLocaleString()}
                    {h.occurred_by_email && <> · by {h.occurred_by_email}</>}
                  </div>
                  {h.reason && <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic' }}>{h.reason}</div>}
                </div>
              ))}
            </Section>
          )}

          {/* Notes — always editable. Saves on blur or Ctrl/Cmd-Enter. */}
          <Section title="Notes">
            <textarea
              value={notesValue}
              onChange={e => setNotesDraft(e.target.value)}
              onBlur={() => {
                if (notesDraft != null && notesDraft !== (permit.notes ?? '')) {
                  patchMut.mutate({ notes: notesDraft || null });
                }
                setNotesDraft(null);
              }}
              onKeyDown={e => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  (e.currentTarget as HTMLTextAreaElement).blur();
                }
              }}
              placeholder="Add notes — context the next operator needs, agency contacts, RFI summaries, anything not captured elsewhere. Ctrl-Enter to save."
              style={{
                width: '100%', minHeight: 70,
                padding: '6px 8px', fontSize: 12,
                border: '1px solid #cbd5e1', borderRadius: 4,
                fontFamily: 'inherit', resize: 'vertical',
                color: '#0f172a',
              }}
            />
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
              Saves on blur. Ctrl/Cmd-Enter also saves.
            </div>
          </Section>

          {/* Feature info — agency-facing fields the operator may want to
              correct after detection (especially for manually-added permits). */}
          <Section title="Feature info">
            <EditableField label="Feature name" value={permit.feature_name}
              onSave={v => patchMut.mutate({ feature_name: v || null })}
              placeholder="e.g. Pond Run waterway crossing" />
            <EditableField label="Feature owner" value={permit.feature_owner}
              onSave={v => patchMut.mutate({ feature_owner: v || null })}
              placeholder="e.g. USACE, Vienna, WV DOT" />
            <EditableField label="Jurisdiction" value={permit.jurisdiction}
              onSave={v => patchMut.mutate({ jurisdiction: v || null })}
              placeholder="federal | state | county | city | utility" />
            <EditableField label="Application ref" value={permit.application_ref}
              onSave={v => patchMut.mutate({ application_ref: v || null })}
              placeholder="e.g. CESPK-RD-2026-00451" />
          </Section>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: '#475569',
        textTransform: 'uppercase', letterSpacing: '.06em',
        paddingBottom: 6, borderBottom: '1px solid #e2e8f0', marginBottom: 6,
      }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

// EditableField — inline text editor used in the "Feature info" section.
// Shows the current value; click → input swap → saves on blur or Enter.
function EditableField({ label, value, onSave, placeholder }: {
  label: string;
  value: string | null;
  onSave: (newValue: string) => void;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', fontSize: 12 }}>
      <span style={{ width: 110, color: '#64748b', flexShrink: 0 }}>{label}</span>
      {editing ? (
        <span style={{ flex: 1, display: 'flex', gap: 4 }}>
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => {
              if (draft !== (value ?? '')) onSave(draft);
              setEditing(false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
              else if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
            }}
            placeholder={placeholder}
            style={{
              flex: 1, padding: '3px 6px', fontSize: 12,
              border: '1px solid #cbd5e1', borderRadius: 3,
            }}
          />
        </span>
      ) : (
        <span style={{ flex: 1, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
          {value ?? <i style={{ color: '#94a3b8' }}>not set</i>}
          <button
            onClick={() => { setDraft(value ?? ''); setEditing(true); }}
            style={{
              padding: '1px 6px', fontSize: 10, background: '#fff',
              border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer',
              color: '#475569',
            }}
          >{value ? 'edit' : '+ set'}</button>
        </span>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', fontSize: 12 }}>
      <span style={{ width: 110, color: '#64748b', flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, color: '#0f172a' }}>{value}</span>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 700,
  color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em',
  borderBottom: '1px solid #e2e8f0',
};
const td: React.CSSProperties = { padding: '5px 8px', verticalAlign: 'middle' };

function CrossingBreakdown({ crossings }: { crossings: any[] }) {
  // Roll up by crossing_kind so the operator sees the shape at a glance.
  const counts = crossings.reduce<Record<string, number>>((acc, x) => {
    const k = x.crossing_kind ?? 'other';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {Object.entries(counts).map(([kind, n]) => (
        <span key={kind} style={{
          padding: '3px 8px', fontSize: 11, borderRadius: 999,
          background: '#f1f5f9', color: '#0f172a',
          border: `1px solid ${CROSSING_KIND_COLOR[kind] ?? '#94a3b8'}`,
        }}>
          <span style={{
            display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
            background: CROSSING_KIND_COLOR[kind] ?? '#94a3b8',
            marginRight: 4, verticalAlign: 'middle',
          }} />
          {CROSSING_KIND_LABEL[kind] ?? kind}: <b>{n}</b>
        </span>
      ))}
    </div>
  );
}
